import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { GraduationCap } from "lucide-react";
import { formatDateTime, Modal } from "./_shared";


const STATUS_OPTIONS = ["pending", "shortlisted", "accepted", "rejected"];


export function StudentChaptersPanel() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/student-chapters/applications")
      .then(({ data }) => setApps(data))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setStatus = async (application_id, status) => {
    try {
      const { data } = await api.patch(
        `/admin/student-chapters/applications/${application_id}`,
        { status, admin_notes: active?.admin_notes || "" },
      );
      toast.success(`Status updated to ${status}`);
      setApps((prev) => prev.map((a) => (a.application_id === application_id ? data : a)));
      if (active?.application_id === application_id) setActive(data);
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <div data-testid="admin-student-chapters">
      <div className="flex items-center gap-3 mb-6">
        <GraduationCap className="w-6 h-6" style={{ color: "var(--ser-primary)" }} />
        <div>
          <p className="eyebrow">Student Chapters</p>
          <h1 className="font-serif-display text-4xl" style={{ color: "var(--ser-primary-dark)" }}>
            Applications
          </h1>
        </div>
      </div>

      {loading ? (
        <p style={{ color: "var(--ser-text-muted)" }}>Loading…</p>
      ) : apps.length === 0 ? (
        <div className="ser-card p-10 text-center">
          <p style={{ color: "var(--ser-text-muted)" }}>
            No applications yet. Submissions from the public form on
            <code className="mx-1">/student-chapters</code> will land here.
          </p>
        </div>
      ) : (
        <div className="ser-card overflow-hidden">
          <table className="w-full text-sm" data-testid="sc-apps-table">
            <thead>
              <tr style={{ backgroundColor: "var(--ser-sand)" }}>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-wider" style={{ color: "var(--ser-teal)" }}>Applicant</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-wider" style={{ color: "var(--ser-teal)" }}>University</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-wider" style={{ color: "var(--ser-teal)" }}>Status</th>
                <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-wider" style={{ color: "var(--ser-teal)" }}>Submitted</th>
                <th className="text-right px-4 py-3 font-bold uppercase text-xs tracking-wider" style={{ color: "var(--ser-teal)" }}></th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.application_id} className="border-t" style={{ borderColor: "var(--ser-border, #eee)" }}>
                  <td className="px-4 py-3">
                    <p className="font-bold" style={{ color: "var(--ser-primary-dark)" }}>{a.full_name}</p>
                    <p className="text-xs" style={{ color: "var(--ser-text-muted)" }}>{a.email}</p>
                  </td>
                  <td className="px-4 py-3" style={{ color: "var(--ser-text-muted)" }}>{a.university}</td>
                  <td className="px-4 py-3">
                    <span className="ser-tag">{a.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: "var(--ser-text-muted)" }}>{formatDateTime(a.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setActive(a)}
                      className="btn-ser-outline text-xs"
                      data-testid={`sc-view-${a.application_id}`}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {active && (
        <Modal onClose={() => setActive(null)} title={`Application · ${active.full_name}`}>
          <Detail label="Email" value={active.email} />
          <Detail label="Phone" value={active.phone} />
          <Detail label="University" value={active.university} />
          <Detail label="Department" value={active.department} />
          <Detail label="Year / position" value={active.year_or_position} />
          <Detail label="SER member" value={active.ser_member} />

          <SectionTitle>Motivation &amp; commitment</SectionTitle>
          <LongDetail label="Motivation" value={active.motivation} />
          <LongDetail label="Why establish a chapter" value={active.interest_reason} />
          <LongDetail label="Events attended" value={active.attended_events} />

          <SectionTitle>Experience</SectionTitle>
          <LongDetail label="Prior experience" value={active.prior_experience} />
          <LongDetail label="Current projects" value={active.current_projects} />
          <LongDetail label="Mentor / professor" value={active.mentor_details} />

          <SectionTitle>Availability</SectionTitle>
          <Detail label="Time commitment" value={active.time_commitment} />
          <Detail label="Long-term (1+ academic year)" value={active.long_term} />

          <SectionTitle>References</SectionTitle>
          {(active.references || []).length === 0 && (
            <p className="text-xs" style={{ color: "var(--ser-text-muted)" }}>None provided.</p>
          )}
          {(active.references || []).map((r, i) => (
            <div key={i} className="text-sm mb-2" style={{ color: "var(--ser-text-muted)" }}>
              <strong style={{ color: "var(--ser-teal)" }}>{r.name || "—"}</strong>
              {r.organisation ? ` · ${r.organisation}` : ""}
              {r.email ? ` · ${r.email}` : ""}
              {r.phone ? ` · ${r.phone}` : ""}
            </div>
          ))}

          <SectionTitle>Decision</SectionTitle>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(active.application_id, s)}
                className={`btn-ser-outline text-xs ${active.status === s ? "" : "opacity-70"}`}
                style={active.status === s ? { backgroundColor: "var(--ser-primary)", color: "white", borderColor: "var(--ser-primary)" } : {}}
                data-testid={`sc-set-status-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}


function Detail({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-2 text-sm border-b" style={{ borderColor: "var(--ser-border, #eee)" }}>
      <span className="font-bold uppercase text-xs tracking-wider" style={{ color: "var(--ser-teal)" }}>{label}</span>
      <span className="col-span-2" style={{ color: "var(--ser-text-muted)" }}>{value || "—"}</span>
    </div>
  );
}

function LongDetail({ label, value }) {
  return (
    <div className="py-2 text-sm">
      <p className="font-bold uppercase text-xs tracking-wider mb-1" style={{ color: "var(--ser-teal)" }}>{label}</p>
      <p className="whitespace-pre-line" style={{ color: "var(--ser-text-muted)" }}>{value || "—"}</p>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <p
      className="eyebrow mt-6 mb-2 pt-3 border-t"
      style={{ borderColor: "var(--ser-border, #eee)" }}
    >
      {children}
    </p>
  );
}
